import { useState, useEffect, useMemo } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useNavigate } from "react-router-dom"
import assetNoWebinar from '@/assets/noWebinar.svg'
import {
  Video, Calendar, Users, Clock, Plus, Radio, CalendarClock,
  CheckCircle2, FileEdit, Filter, Search, X, LayoutGrid, List,
  Eye, Edit3, Play,
} from "lucide-react"
import { EmptyState } from "@/clientWeb/components/ui/empty-state"
import { WebinarCardSkeleton } from "@/clientWeb/components/ui/skeleton-loaders"
import { Button } from "@/clientWeb/components/ui/button"
import { Card } from "@/clientWeb/components/ui/card"
import { Input } from "@/clientWeb/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/clientWeb/components/ui/select"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import type { Webinar } from "./mockData"
import { useWebinarStore } from "./store/webinar.store"
import { useAuthStore } from "@/features/auth/store/auth.store"

type ViewMode = "card" | "list"
type StatusFilter = "all" | "upcoming" | "ongoing" | "completed" | "draft"

export default function WebinarList() {
  const t = useTranslation()
  const navigate = useNavigate()
  const authUser = useAuthStore((s) => s.user)
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all")
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearchExpanded, setIsSearchExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [viewMode, setViewMode] = useState<ViewMode>("card")

  const joinWebinar = (id: string) => {
    const name = authUser?.name || "Guest"
    navigate(`/meeting/${id}?name=${encodeURIComponent(name)}`)
  }

  // Hydrate live status (upcoming → ongoing → completed) on mount so cards
  // reflect the current time without requiring a manual refresh.
  const refreshStatuses = useWebinarStore((s) => s.refreshStatuses)
  const webinarsFromStore = useWebinarStore((s) => s.webinars)

  useEffect(() => {
    refreshStatuses()
  }, [refreshStatuses])

  useEffect(() => {
    setIsLoading(true)
    const timer = setTimeout(() => setIsLoading(false), 400)
    return () => clearTimeout(timer)
  }, [statusFilter])

  const filterLabels: Record<StatusFilter, string> = {
    all: t("webinar.all"),
    upcoming: t("webinar.upcoming"),
    ongoing: t("webinar.ongoing"),
    completed: t("webinar.completed"),
    draft: t("webinar.draft"),
  }

  // Adapt persisted WebinarRecord shape → render-friendly Webinar shape.
  // Newest first (createdAt desc).
  const webinars: Webinar[] = useMemo(() => {
    return [...webinarsFromStore]
      .sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
      .map((w) => ({
        id: w.id,
        title: w.title,
        description: w.description,
        startTime: new Date(w.startTimeMs).toISOString(),
        endTime: new Date(w.endTimeMs).toISOString(),
        duration: w.duration,
        status: w.status as Webinar["status"],
        registrants: Array.isArray(w.registrants) ? w.registrants.length : 0,
        maxRegistrants: w.maxRegistrants,
        host: { name: w.hostName || "Unknown", email: w.hostEmail || "" },
        thumbnail: w.thumbnail,
        isRecurring: !!w.recurring,
        category: "Product",
      }))
  }, [webinarsFromStore])

  const filteredWebinars = useMemo(() => {
    return webinars.filter((webinar) => {
      const matchesStatus = statusFilter === "all" || webinar.status === statusFilter
      const matchesSearch = searchQuery === "" ||
        webinar.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        webinar.description.toLowerCase().includes(searchQuery.toLowerCase())
      return matchesStatus && matchesSearch
    })
  }, [webinars, statusFilter, searchQuery])

  const getStatusBadge = (status: Webinar["status"]) => {
    const statusConfig = {
      upcoming: {
        icon: <CalendarClock className="w-3 h-3" />,
        label: t("webinar.upcoming"),
        className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white text-xs font-semibold shadow-md shadow-blue-500/20",
      },
      ongoing: {
        icon: <Radio className="w-3 h-3" />,
        label: t("webinar.live"),
        className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs font-semibold shadow-md shadow-emerald-500/20 animate-pulse",
      },
      completed: {
        icon: <CheckCircle2 className="w-3 h-3" />,
        label: t("webinar.completed"),
        className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-slate-500 to-slate-600 text-white text-xs font-semibold shadow-md shadow-slate-500/20",
      },
      draft: {
        icon: <FileEdit className="w-3 h-3" />,
        label: t("webinar.draft"),
        className: "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-xs font-semibold shadow-md shadow-amber-500/20",
      },
    }
    const config = statusConfig[status]
    return (
      <div className={config.className}>
        {config.icon}
        {config.label}
      </div>
    )
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    const day = date.toLocaleDateString("en-US", { weekday: "short" })
    const datePart = date.toLocaleDateString("en-US", { day: "numeric", month: "short" })
    const timePart = date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    return `${day}, ${datePart}, ${timePart}`
  }

  return (
    <div className="h-full w-full px-4 sm:px-6 py-4">
      <div className="w-full h-full flex flex-col animate-fade-in">

        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 px-1 gap-3">
          <span className="text-md sm:text-xl font-bold text-foreground tracking-tight truncate">
            {t("webinar.webinars")} <span className="font-semibold text-slate-900 dark:text-foreground/80 dark:text-white">({filteredWebinars.length})</span>
          </span>
          <div className="flex flex-1 items-start sm:items-center gap-2 justify-start sm:justify-end w-full sm:w-auto flex-wrap">
            {/* Date range filter */}
            {/* <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as DateFilter)}>
              <SelectTrigger className="h-8 px-3 w-auto min-w-[120px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {(["today", "yesterday", "lastWeek", "lastMonth"] as DateFilter[]).map((d) => (
                  <SelectItem key={d} value={d} className="text-xs">{DATE_FILTER_LABELS[d]}</SelectItem>
                ))}
              </SelectContent>
            </Select> */}

            {/* Mobile: Search icon button */}
            <div className="sm:flex-1 sm:hidden">
              {isSearchExpanded ? (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="text"
                      placeholder={t("webinar.searchWebinars")}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9 h-9 w-full"
                      autoFocus
                    />
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => setIsSearchExpanded(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button variant="ghost" size="icon" onClick={() => setIsSearchExpanded(true)}>
                  <Search className="w-4 h-4 text-muted-foreground" />
                </Button>
              )}
            </div>
            {/* Desktop: Search Input */}
            <div className="relative hidden sm:block flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t("webinar.searchWebinars")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 w-full sm:w-64"
              />
            </div>
            <div className={`flex gap-1 sm:gap-3 ${isSearchExpanded ? 'hidden sm:flex' : ''}`}>
              {/* Mobile: Status filter icon dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild className="lg:hidden">
                  <Button variant="ghost" size="icon" className="relative shrink-0">
                    <Filter className="w-4 h-4 text-muted-foreground" />
                    {statusFilter !== "all" && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full" />}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {(["all", "upcoming", "ongoing", "completed", "draft"] as const).map((filter) => (
                    <DropdownMenuItem
                      key={filter}
                      onClick={() => setStatusFilter(filter)}
                      className={statusFilter === filter ? "font-semibold text-primary" : ""}
                    >
                      {filterLabels[filter]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
              {webinars.length > 0 && (
                <Button
                  onClick={() => navigate("/webinar/analytics")}
                  variant="ghost"
                  size={'sm'}
                >
                  <img src="/icons/analyticswebinar.svg" alt='' className="w-5 h-5 sm:w-3 sm:h-3 mr-2 sm:mr-0" />
                  <span className="hidden sm:block text-xs md:text-sm">{t("analytics.title")}</span>
                </Button>
              )}
              {/* Desktop: Status filter select */}
              <div className="hidden lg:block">
                <Select value={statusFilter} onValueChange={(value) => setStatusFilter(value as StatusFilter)}>
                  <SelectTrigger className="h-8 px-4 justify-between max-w-44">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent align="end" className="w-44">
                    {(["all", "upcoming", "ongoing", "completed", "draft"] as const).map((filter) => (
                      <SelectItem key={filter} value={filter}>
                        {filterLabels[filter]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {/* View mode toggle */}
              <div className="hidden sm:flex items-center bg-muted rounded-lg p-1">
                <button
                  onClick={() => setViewMode('card')}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'card'
                      ? 'bg-card shadow-sm text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title="Card view"
                >
                  <LayoutGrid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded-md transition-all ${
                    viewMode === 'list'
                      ? 'bg-card shadow-sm text-primary'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                  title="List view"
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
              <Button onClick={() => navigate("/webinar/create")}>
                <Plus className="w-5 h-5 mr-2 hidden sm:block" />
                {t("webinar.create")}
              </Button>
            </div>
          </div>
        </div>

        {/* Webinar Content */}
        <div className="flex-1 min-h-0 overflow-y-auto scroll-smooth">
          {isLoading ? (
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 pb-2">
              {Array.from({ length: 4 }).map((_, i) => <WebinarCardSkeleton key={i} />)}
            </div>
          ) : filteredWebinars.length === 0 ? (
            <div className="flex flex-col h-full justify-center">
              <EmptyState
                image={assetNoWebinar}
                size='xxl'
                title={searchQuery ? t("webinar.noWebinarsFound") : statusFilter !== "all" ? t("webinar.noWebinarsFound") : t("webinar.noWebinarsYet")}
                description={
                  searchQuery
                    ? ""
                    : statusFilter !== "all"
                      ? t("webinar.noFilteredWebinars")
                      : t("webinar.getStartedWebinar")
                }
                actionLabel={searchQuery ? undefined : statusFilter === "all" ? t("webinar.createWebinar") : undefined}
                onAction={searchQuery ? undefined : statusFilter === "all" ? () => navigate("/webinar/create") : undefined}
              />
            </div>
          ) : viewMode === 'card' ? (
            /* ── Card View ── */
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4 pb-2">
              {filteredWebinars.map((webinar, index) => (
                <Card
                  key={webinar.id}
                  className="overflow-hidden border-stroke-100 dark:border-border/70 dark:border-border bg-card/90 dark:bg-card backdrop-blur-sm hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10 transition-all duration-300 group animate-fade-in"
                  style={{ animationDelay: `${index * 60}ms`, animationFillMode: "both" }}
                >
                  {/* Thumbnail */}
                  <div
                    className="relative h-40 overflow-hidden cursor-pointer"
                    onClick={() => navigate(`/webinar/${webinar.id}`)}
                  >
                    {webinar.thumbnail ? (
                      <>
                        <img src={webinar.thumbnail} alt={webinar.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute top-3 right-3">{getStatusBadge(webinar.status)}</div>
                      </>
                    ) : (
                      <>
                        <div className="w-full h-full bg-[#E0E0E0] dark:bg-[#2A2A2A] flex items-center justify-center">
                          <Video className="w-12 h-12 text-primary/40 dark:text-foreground/50" />
                        </div>
                        <div className="absolute top-3 right-3">{getStatusBadge(webinar.status)}</div>
                      </>
                    )}
                  </div>
                  {/* Content */}
                  <div className="p-3">
                    <h3
                      className="font-semibold text-md text-slate-900 dark:text-foreground/80 dark:text-white mb-2 line-clamp-1 cursor-pointer hover:text-primary transition-colors"
                      onClick={() => navigate(`/webinar/${webinar.id}`)}
                    >
                      {webinar.title}
                    </h3>
                    <p className="text-xs text-slate-600 dark:text-foreground mb-4 line-clamp-2">{webinar.description}</p>
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDateTime(webinar.startTime)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                        <Clock className="w-4 h-4" />
                        <span>{webinar.duration} {t("common.minutes")}</span>
                        <div className="flex pl-2 items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{webinar.registrants}</span>
                        </div>
                      </div>
                    </div>
                    {/* Host + action */}
                    <div className="flex items-center gap-3 pt-2 border-t border-stroke-100 dark:border-border">
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-primary">{webinar.host.name.charAt(0)}</span>
                      </div>
                      <p className="text-xs font-medium text-slate-900 dark:text-white truncate flex-1">{webinar.host.name}</p>
                      {webinar.status === 'ongoing' && (
                        <Button size="sm" className="text-xs h-7 px-3"
                          onClick={(e) => { e.stopPropagation(); joinWebinar(webinar.id) }}>
                          <Radio className="w-3 h-3 mr-1" /> {t("common.join")}
                        </Button>
                      )}
                      {webinar.status === 'upcoming' && (
                        <Button size="sm" className="text-xs h-7 px-3 bg-emerald-500 hover:bg-emerald-600 text-white"
                          onClick={(e) => { e.stopPropagation(); joinWebinar(webinar.id) }}>
                          <Play className="w-3 h-3 mr-1" /> Start
                        </Button>
                      )}
                      {webinar.status === 'completed' && (
                        <Button size="sm" variant="ghost" className="text-xs h-7 px-2 text-primary"
                          onClick={(e) => { e.stopPropagation(); navigate(`/webinar/${webinar.id}`) }}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {webinar.status === 'draft' && (
                        <Button size="sm" variant="outline" className="text-xs h-7 px-3"
                          onClick={(e) => { e.stopPropagation(); navigate(`/webinar/create?edit=${webinar.id}`) }}>
                          <Edit3 className="w-3 h-3 mr-1" /> {t("common.edit")}
                        </Button>
                      )}
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          ) : (
            /* ── List View ── */
            <div className="flex flex-col gap-2 pb-2">
              {filteredWebinars.map((webinar, index) => {
                const statusBorderMap: Record<string, string> = {
                  ongoing: 'border-l-emerald-500',
                  upcoming: 'border-l-amber-500',
                  completed: 'border-l-slate-400',
                  draft: 'border-l-orange-400',
                }
                return (
                  <div
                    key={webinar.id}
                    onClick={() => navigate(`/webinar/${webinar.id}`)}
                    className={`flex items-center gap-3 p-3 shadow-sm rounded-[14px] bg-card border-l-4 transition-all duration-180 cursor-pointer hover:border-indigo-100 dark:hover:border-indigo-400 hover:bg-indigo-50/30 dark:hover:bg-card hover:shadow-md animate-fade-in ${statusBorderMap[webinar.status] ?? 'border-l-slate-400'}`}
                    style={{ animationDelay: `${index * 50}ms` }}
                  >
                    {/* Thumbnail / icon */}
                    <div className="w-10 h-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
                      <Video className="w-5 h-5 text-muted-foreground" />
                    </div>

                    {/* Left — title + meta (~40%) */}
                    <div className="flex-[2] min-w-0">
                      <span className="text-sm font-semibold text-slate-900 dark:text-white truncate block">{webinar.title}</span>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5 flex-wrap">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 shrink-0" />{formatDateTime(webinar.startTime)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 shrink-0" />{webinar.duration} {t('common.minutes')}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3 shrink-0" />{webinar.registrants}
                        </span>
                      </div>
                    </div>

                    {/* Status — center (~20%) */}
                    <div className="hidden sm:flex flex-1 items-center justify-center">
                      {getStatusBadge(webinar.status)}
                    </div>

                    {/* Host (~20%) */}
                    <div className="hidden sm:flex flex-1 items-center gap-2 min-w-0">
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <span className="text-xs font-medium text-primary">{webinar.host.name.charAt(0)}</span>
                      </div>
                      <span className="text-xs text-muted-foreground truncate">{webinar.host.name}</span>
                    </div>

                    {/* Action (~20%) */}
                    <div className="flex-1 flex justify-end" onClick={(e) => e.stopPropagation()}>
                      {webinar.status === 'ongoing' && (
                        <Button size="sm"
                          onClick={() => joinWebinar(webinar.id)}>
                          <Radio className="w-3.5 h-3.5 mr-1.5" /> {t('webinar.join')}
                        </Button>
                      )}
                      {webinar.status === 'upcoming' && (
                        <Button size="sm"
                          className="bg-emerald-500 hover:bg-emerald-600 text-white"
                          onClick={() => joinWebinar(webinar.id)}>
                          <Play className="w-3.5 h-3.5 mr-1.5" /> {t('webinar.start')}
                        </Button>
                      )}
                      {webinar.status === 'completed' && (
                        <Button size="sm" variant="ghost" className="text-primary"
                          onClick={() => navigate(`/webinar/${webinar.id}`)}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      {webinar.status === 'draft' && (
                        <Button size="sm" variant="outline"
                          onClick={() => navigate(`/webinar/create?edit=${webinar.id}`)}>
                          <Edit3 className="w-3.5 h-3.5 mr-1.5" /> {t('common.edit')}
                        </Button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}
